<template>
  <div>
    <PageHeader
      image="header-5"
      withMobile
      :title="$lang('title')"
      :breadcrumbs="$lang('breadcr')"
    />
    <section class="py-7">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-12 col-md-10 col-lg-8 text-center">
            <h2 class="mb-4">{{ $lang('test_title') }}</h2>
            <p class="lead mb-5">{{ $lang('test_description') }}</p>
          </div>
        </div>

        <div class="row justify-content-center">
          <div class="col-12 col-md-10 col-lg-8">
            <div class="card shadow-lg border-0 mb-5">
              <div class="card-body p-5">
                <div v-if="!testCompleted">
                  <div class="mb-4">
                    <h4 class="text-blue2">Page {{ currentPage + 1 }} of {{ totalPages }}</h4>
                    <div class="d-flex justify-content-between mb-2">
                      <span>Questions {{ getCurrentPageStartIndex() + 1 }}-{{ Math.min(getCurrentPageStartIndex() + 5, questions.length) }}</span>
                      <span>{{ questions.length }} questions total</span>
                    </div>
                    <div class="progress mb-3">
                      <div 
                        class="progress-bar bg-blue2" 
                        role="progressbar" 
                        :style="`width: ${((currentPage + 1) / totalPages) * 100}%`" 
                        :aria-valuenow="((currentPage + 1) / totalPages) * 100" 
                        aria-valuemin="0" 
                        aria-valuemax="100">
                      </div>
                    </div>
                  </div>

                  <div class="question-container">
                    <!-- Show 5 questions per page -->
                    <div v-for="(question, qIndex) in getCurrentPageQuestions()" :key="qIndex" class="mb-5">
                      <h5 class="mb-3 text-blue1 fw-semibold">{{ question.question }}</h5>
                      
                      <div class="options-container">
                        <div 
                          v-for="(option, index) in question.options" 
                          :key="index" 
                          class="form-check mb-2"
                        >
                          <input 
                            class="form-check-input" 
                            type="radio" 
                            :name="`question${getCurrentPageStartIndex() + qIndex}`" 
                            :id="`question${getCurrentPageStartIndex() + qIndex}_option${index}`"
                            :value="option"
                            v-model="userAnswers[getCurrentPageStartIndex() + qIndex]"
                          >
                          <label class="form-check-label" :for="`question${getCurrentPageStartIndex() + qIndex}_option${index}`">
                            {{ option }}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex justify-content-between mt-5">
                      <button 
                        class="btn btn-black1" 
                        @click="previousPage" 
                        :disabled="currentPage === 0"
                      >
                        Previous Page
                      </button>
                      <button 
                        class="btn btn-blue-grad-2" 
                        @click="currentPage === totalPages - 1 ? showResults() : nextPage()"
                      >
                        {{ currentPage === totalPages - 1 ? 'Show Results' : 'Next Page' }}
                      </button>
                    </div>
                    

                  </div>
                </div>

                <div v-else class="text-center">
                  <div class="mb-4">
                    <i class="fas fa-award text-green1 fa-5x"></i>
                  </div>
                  <h3 class="mb-3 text-blue2">Your English Level Assessment</h3>
                  
                  <div class="result-card p-4 mb-4">
                    <div class="score-section mb-3">
                      <h4 class="text-blue1">Your Score: <h3 class="text-green1 text-lg">{{ score }} / {{ questions.length }}</h3></h4>
                    </div>
                    
                    <div class="level-section mb-4">
                      <h5 class="text-blue2 mb-2">Your English Level:</h5>
                      <div class="level-badge mb-3">{{ userLevel }}</div>
                      <h5 class="text-blue2 text-bold">
                        Congratulations! This result is indicative of a student using English at {{ userLevel }} level!
                      </h5>
                    </div>
                    
                    <div class="disclaimer-section">
                      <p class="text-bold text-2xl">
                        *This level-assessment is indicative only. A full assessment of your English language skills will be conducted as part of your induction process at NED College and an appropriate level of study assigned to you.
                      </p>
                    </div>
                  </div>
                  
                  <div class="actions-section">
                    <button class="btn btn-blue-grad-2 mt-2 me-2" @click="goHome">
                      Return to Homepage
                    </button>
                    <button class="btn btn-green-grad mt-2" @click="contactUs">
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader";
import { trackEvent, StandardEvents } from "@/utils/facebookPixel";

export default {
  components: {
    PageHeader
  },
  data() {
    return {
      currentPage: 0,
      questionsPerPage: 5,
      userAnswers: [],
      testCompleted: false,
      score: 0,
      userLevel: '',
      levelRanges: [
        { min: 1, max: 5, level: 'Beginner / Pre-A1' },
        { min: 6, max: 10, level: 'Elementary' },
        { min: 11, max: 14, level: 'Pre-Intermediate' },
        { min: 15, max: 19, level: 'Intermediate' },
        { min: 20, max: 22, level: 'Upper-Intermediate' },
        { min: 23, max: 25, level: 'Advanced' }
      ],
      questions: [
        // Page 1
        {
          question: "1. I have never __________________ to Ireland before.",
          options: ["A. went", "B. go", "C. been"],
          correctAnswer: "C. been"
        },
        {
          question: "2. My _________________ is really early in the morning.",
          options: ["A. flight", "B. fly", "C. flies"],
          correctAnswer: "A. flight"
        },
        {
          question: "3. How _______________ is a ticket to Dublin?",
          options: ["A. cost", "B. much", "C. Euro"],
          correctAnswer: "B. much"
        },
        {
          question: "4. My dream is to travel ________________ the world",
          options: ["A. to", "B. in", "C. around"],
          correctAnswer: "C. around"
        },
        {
          question: "5. Elisabeth __________________ born in Belfast.",
          options: ["A. is", "B. has", "C. was"],
          correctAnswer: "C. was"
        },
        
        // Page 2
        {
          question: "6. Before my trip, I bought a travel guide from the ____________.",
          options: ["A. bookshop", "B. library", "C. bookcase"],
          correctAnswer: "A. bookshop"
        },
        {
          question: "7. Alice put the flowers in a ___________________.",
          options: ["A. glass", "B. vase", "C. jar"],
          correctAnswer: "B. vase"
        },
        {
          question: "8. My father _________________ to work in a school.",
          options: ["A. usually", "B. used", "C. use"],
          correctAnswer: "B. used"
        },
        {
          question: "9. Stephen was late for work because he ________________ the bus.",
          options: ["A. lose", "B. lost", "C. missed"],
          correctAnswer: "C. missed"
        },
        {
          question: "10. Guinness is __________________ in Dublin.",
          options: ["A. made", "B. make", "C. makes"],
          correctAnswer: "A. made"
        },
        
        // Page 3
        {
          question: "11. Cork is the _______________ county in Ireland.",
          options: ["A. large", "B. larger", "C. largest"],
          correctAnswer: "C. largest"
        },
        {
          question: "12. ______________ are 27 countries in the European Union.",
          options: ["A. Here", "B. There", "C. Where"],
          correctAnswer: "B. There"
        },
        {
          question: "13. The plane _________________ at 8.00 AM.",
          options: ["A. land", "B. lands", "C. landing"],
          correctAnswer: "B. lands"
        },
        {
          question: "14. If you study hard you _____________ succeed!",
          options: ["A. will", "B. would", "C. are"],
          correctAnswer: "A. will"
        },
        {
          question: "15. My mother lives in Kilkenny. _______________ name is Maura.",
          options: ["A. Her", "B. She", "C. Hers"],
          correctAnswer: "A. Her"
        },
        
        // Page 4
        {
          question: "16. Don't forget! In Ireland cars_________________ on the left.",
          options: ["A. ride", "B. drive", "C. turn"],
          correctAnswer: "B. drive"
        },
        {
          question: "17. I promise I _______________ phone you every week.",
          options: ["A. would", "B. must", "C. will"],
          correctAnswer: "C. will"
        },
        {
          question: "18. If you are lucky, you ________________ see a leprechaun!",
          options: ["A. might", "B. are", "C. can't"],
          correctAnswer: "A. might"
        },
        {
          question: "19. There's a surprise waiting ___________ you here!",
          options: ["A. to", "B. for", "C. of"],
          correctAnswer: "B. for"
        },
        {
          question: "20. I want _______________ win this competition!",
          options: ["A. to", "B. for", "C. a"],
          correctAnswer: "A. to"
        },
        
        // Page 5
        {
          question: "21. Marian had ____________________ left the flat when it started to rain.",
          options: ["A. quietly", "B. quickly", "C. hardly"],
          correctAnswer: "C. hardly"
        },
        {
          question: "22. What _____________________ you like to eat?",
          options: ["A. can", "B. could", "C. would"],
          correctAnswer: "C. would"
        },
        {
          question: "23. On Tuesdays and Fridays, Alex ________________ running.",
          options: ["A. going", "B. goes", "C. go"],
          correctAnswer: "B. goes"
        },
        {
          question: "24. You __________________ have won if you hadn't cheated",
          options: ["A. wouldn't", "B. didn't", "C. won't"],
          correctAnswer: "A. wouldn't"
        },
        {
          question: "25. Classes ____________________ for three hours",
          options: ["A. start", "B. last", "C. take"],
          correctAnswer: "B. last"
        }
      ]
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.questions.length / this.questionsPerPage);
    }
  },
  methods: {
    getCurrentPageStartIndex() {
      return this.currentPage * this.questionsPerPage;
    },
    getCurrentPageQuestions() {
      const startIndex = this.getCurrentPageStartIndex();
      return this.questions.slice(startIndex, startIndex + this.questionsPerPage);
    },
    determineLevel(score) {
      for (const range of this.levelRanges) {
        if (score >= range.min && score <= range.max) {
          return range.level;
        }
      }
      return score === 0 ? 'Beginner / Pre-A1' : 'Advanced';
    },
    previousPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++;
      }
    },
    showResults() {
      // Calculate score
      this.calculateScore();
      
      // Determine user level based on score
      this.userLevel = this.determineLevel(this.score);
      
      // Store test results
      const userData = JSON.parse(localStorage.getItem('englishTestUser') || '{}');
      const testResults = {
        ...userData,
        score: this.score,
        totalQuestions: this.questions.length,
        level: this.userLevel,
        completedAt: new Date().toISOString()
      };
      
      localStorage.setItem('englishTestResults', JSON.stringify(testResults));
      
      // Track test completion with Facebook Pixel
      trackEvent(StandardEvents.COMPLETE_REGISTRATION, {
        content_name: 'English Test Completion',
        content_category: 'Test',
        page: document.title,
        score: `${this.score}/${this.questions.length}`,
        level: this.userLevel
      });
      
      this.testCompleted = true;
    },
    calculateScore() {
      this.score = 0;
      this.questions.forEach((question, index) => {
        if (this.userAnswers[index] === question.correctAnswer) {
          this.score++;
        }
      });
      return this.score;
    },
    contactUs() {
      this.$router.push({ name: 'contact_us' });
    },
    goHome() {
      this.$router.push({ name: 'home' });
    }
  },
  mounted() {
    // Initialize empty answers array
    this.userAnswers = new Array(this.questions.length).fill('');
    
    // Make sure all questions on the current page have empty answers initialized
    this.getCurrentPageQuestions().forEach((_, index) => {
      const globalIndex = this.getCurrentPageStartIndex() + index;
      if (!this.userAnswers[globalIndex]) {
        this.userAnswers[globalIndex] = '';
      }
    });
  }
};
</script>

<style scoped>
.question-container {
  min-height: 300px;
}

.options-container {
  text-align: left;
}

.form-check-label {
  cursor: pointer;
  padding-left: 0.5rem;
  font-size: 0.875rem;
}

.progress {
  height: 10px;
  border-radius: var(--radius-xs);
}

.card {
  border-radius: var(--radius-sm);
}

.form-check-input:checked {
  background-color: var(--blue2);
  border-color: var(--blue2);
}

h2 {
  font-size: 1.75rem;
  font-weight: 700;
}

h3 {
  font-size: 1.5rem;
  font-weight: 600;
}

h4 {
  font-size: 1.25rem;
  font-weight: 600;
}

h5 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--black1);
}

p {
  font-size: 0.875rem;
}

.lead {
  font-size: 1rem;
}

span {
  font-size: 0.8rem;
  color: var(--black2);
}

.result-card {
  background-color: white;
  border-radius: var(--radius-xs);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
}

.score-section h4 {
  font-weight: 600;
}

.level-badge {
  display: inline-block;
  background: var(--blue-grad-2);
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  padding: 0.5rem 1.5rem;
  border-radius: var(--radius-xs);
}

.congratulations-text {
  font-size: 1rem;
  color: var(--black1);
  margin-top: 1rem;
}

.disclaimer-text {
  font-size: 0.8rem;
  color: var(--black2);
  font-style: italic;
}

.actions-section {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.scoring-system {
  background-color: rgba(0, 18, 76, 0.03);
  border-radius: var(--radius-xs);
  padding: 1rem;
}

.score-range {
  display: inline-block;
  background-color: var(--blue1);
  color: white;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  min-width: 45px;
  text-align: center;
  margin-right: 0.5rem;
}
</style>
